import React, { useRef, useState } from "react";
import axios from "axios";
import { endpoints } from "../apiConfig";
import { getAuthHeader } from "../session";
import { printElement } from "../printing";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary, inputBase } from "../theme";

// Name, street address and phone of the restaurant — printed at the top of every
// receipt. Falls back to just the business name saved in the login session.
function RestaurantHeader({ restaurant, compact = false }) {
  const sessionName =
    JSON.parse(sessionStorage.getItem("staff_tenant") || "null")?.business_name ||
    JSON.parse(localStorage.getItem("tenant") || "null")?.business_name;
  const name = restaurant?.name || sessionName;
  if (!name) return null;
  const lines = restaurant?.address_lines || [];
  return (
    <div style={compact ? styles.restaurantBlockCompact : styles.restaurantBlock} data-testid="receipt-restaurant">
      <div style={styles.restaurantName}>{name}</div>
      {lines.map((line, i) => <div key={i} style={styles.restaurantLine}>{line}</div>)}
      {restaurant?.phone && <div style={styles.restaurantLine}>{restaurant.phone}</div>}
    </div>
  );
}

export default function ReceiptView({ receipt, restaurant }) {
  const [showSplit, setShowSplit] = useState(false);
  const cardRef = useRef(null);
  const header = restaurant || receipt.restaurant;
  const staffTenant = JSON.parse(sessionStorage.getItem("staff_tenant") || "null");
  const ownerTenant = JSON.parse(localStorage.getItem("tenant") || "null");
  const splitBillEnabled = staffTenant?.split_bill_enabled ?? ownerTenant?.split_bill_enabled;

  return (
    <div style={styles.card} ref={cardRef} className="receipt-card">
      <RestaurantHeader restaurant={header} />
      <div style={styles.header}>
        <h2 style={styles.title}>Receipt</h2>
        <div style={styles.subtitle}>{receipt.table_label}</div>
      </div>

      <div style={styles.itemList}>
        {receipt.items.map((item, i) => (
          <div key={i} style={styles.itemRow}>
            <span>{item.quantity}× {item.item_name}</span>
            <span>${(item.unit_price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
      </div>

      <div style={styles.totalsBlock}>
        <div style={styles.totalRow}><span>Subtotal</span><span>${receipt.subtotal.toFixed(2)}</span></div>
        <div style={styles.totalRow}><span>Tax</span><span>${receipt.tax_amount.toFixed(2)}</span></div>
        <div style={styles.totalRow}><span>Gratuity</span><span>${receipt.gratuity_amount.toFixed(2)}</span></div>
        {receipt.tip_amount > 0 && (
          <div style={styles.totalRow}><span>Tip</span><span>${receipt.tip_amount.toFixed(2)}</span></div>
        )}
        <div style={styles.grandTotalRow}><span>Total</span><span>${receipt.total_amount.toFixed(2)}</span></div>
      </div>

      <div className="no-print" style={{ display: "flex", gap: space[2], marginTop: space[3] }}>
        <button style={{ ...buttonSecondary, flex: 1 }} onClick={() => printElement(cardRef.current, "receipt")}>Print</button>
        {splitBillEnabled && (
          <button style={{ ...buttonPrimary, flex: 1 }} onClick={() => setShowSplit(true)}>Split Bill</button>
        )}
      </div>

      {showSplit && (
        <SplitBillModal orderId={receipt.order_id} items={receipt.items} restaurant={header} onClose={() => setShowSplit(false)} />
      )}
    </div>
  );
}

function SplitBillModal({ orderId, items, restaurant, onClose }) {
  const [mode, setMode] = useState("equal");
  const [ways, setWays] = useState(2);
  const [customAmounts, setCustomAmounts] = useState(["", ""]);
  // By-item mode: splitNames is the list of split labels; assignments
  // maps each item's array index -> which split index it belongs to
  // (or null if not yet assigned).
  const [splitNames, setSplitNames] = useState(["Guest 1", "Guest 2"]);
  const [assignments, setAssignments] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const authHeader = () => getAuthHeader();

  const assignItem = (itemIndex, splitIndex) => {
    setAssignments((prev) => ({ ...prev, [itemIndex]: splitIndex }));
  };

  const addSplitName = () => {
    setSplitNames([...splitNames, `Guest ${splitNames.length + 1}`]);
  };

  const calculate = async () => {
    setError("");
    try {
      const payload = { order_id: orderId, mode };
      if (mode === "equal") payload.ways = parseInt(ways, 10) || 2;
      if (mode === "custom") payload.amounts = customAmounts.map((a) => parseFloat(a) || 0);
      if (mode === "by_item") {
        const itemGroups = splitNames.map((_, splitIndex) =>
          items
            .map((item, itemIndex) => ({ item, itemIndex }))
            .filter(({ itemIndex }) => assignments[itemIndex] === splitIndex)
            .map(({ item }) => item.id)
        );
        const unassigned = items.filter((_, i) => assignments[i] === undefined);
        if (unassigned.length > 0) {
          setError(`Assign all items first. ${unassigned.length} item(s) still unassigned.`);
          return;
        }
        payload.item_groups = itemGroups;
      }

      const res = await axios.post(endpoints.orderSplits, payload, { headers: authHeader() });
      setResult(res.data.splits);
    } catch (err) {
      setError(err.response?.data?.error || "Could not calculate split.");
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, width: mode === "by_item" ? 420 : 320 }}>
        <h3 style={{ fontFamily: fonts.display, margin: 0 }}>Split Bill</h3>

        {!result ? (
          <>
            <div style={styles.modeRow}>
              {["equal", "by_item", "custom", "full"].map((m) => (
                <button
                  key={m}
                  style={{ ...styles.modeBtn, ...(mode === m ? styles.modeBtnActive : {}) }}
                  onClick={() => setMode(m)}
                >
                  {m === "equal" ? "Equal" : m === "by_item" ? "By Item" : m === "custom" ? "Custom" : "Full"}
                </button>
              ))}
            </div>

            {mode === "equal" && (
              <div style={styles.qtyRow}>
                <span style={{ fontFamily: fonts.body, fontSize: 13 }}>Split ways:</span>
                <input
                  style={{ ...inputBase, width: 60 }}
                  type="number"
                  min="2"
                  value={ways}
                  onChange={(e) => setWays(e.target.value)}
                />
              </div>
            )}

            {mode === "custom" && (
              <div style={{ display: "flex", flexDirection: "column", gap: space[2] }}>
                {customAmounts.map((amt, i) => (
                  <input
                    key={i}
                    style={inputBase}
                    type="number"
                    step="0.01"
                    placeholder={`Split ${i + 1} amount`}
                    value={amt}
                    onChange={(e) => {
                      const next = [...customAmounts];
                      next[i] = e.target.value;
                      setCustomAmounts(next);
                    }}
                  />
                ))}
                <button style={buttonSecondary} onClick={() => setCustomAmounts([...customAmounts, ""])}>
                  + Add Split
                </button>
              </div>
            )}

            {mode === "by_item" && (
              <div>
                <div style={styles.splitNameRow}>
                  {splitNames.map((name, i) => (
                    <input
                      key={i}
                      style={{ ...inputBase, width: 90, fontSize: 12 }}
                      value={name}
                      onChange={(e) => {
                        const next = [...splitNames];
                        next[i] = e.target.value;
                        setSplitNames(next);
                      }}
                    />
                  ))}
                  <button style={styles.addSplitBtn} onClick={addSplitName}>+ Split</button>
                </div>
                <p style={styles.byItemHint}>Tap which split each item belongs to:</p>
                <div style={styles.itemAssignList}>
                  {items.map((item, itemIndex) => (
                    <div key={itemIndex} style={styles.itemAssignRow}>
                      <span style={{ fontFamily: fonts.body, fontSize: 13, flex: 1 }}>
                        {item.quantity}× {item.item_name}
                      </span>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {splitNames.map((name, splitIndex) => (
                          <button
                            key={splitIndex}
                            onClick={() => assignItem(itemIndex, splitIndex)}
                            style={{
                              ...styles.assignBtn,
                              ...(assignments[itemIndex] === splitIndex ? styles.assignBtnActive : {}),
                            }}
                          >
                            {name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && <div style={styles.error}>{error}</div>}
            <div style={{ display: "flex", gap: space[2] }}>
              <button style={{ ...buttonSecondary, flex: 1 }} onClick={onClose}>Cancel</button>
              <button style={{ ...buttonPrimary, flex: 1 }} onClick={calculate}>Calculate</button>
            </div>
          </>
        ) : (
          <>
            {result.map((split, i) => (
              <SplitReceipt key={i} split={split} restaurant={restaurant} />
            ))}
            <button style={buttonPrimary} onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

function SplitReceipt({ split, restaurant }) {
  const cardRef = useRef(null);
  return (
    <div style={styles.splitReceiptCard} ref={cardRef} className="receipt-card">
      <RestaurantHeader restaurant={restaurant} compact />
      <div style={styles.splitReceiptHeader}>{split.label}</div>
      <div style={styles.totalRow}><span>Subtotal</span><span>${split.amount.toFixed(2)}</span></div>
      <div style={styles.totalRow}><span>Tax</span><span>${split.tax_share.toFixed(2)}</span></div>
      <div style={styles.totalRow}><span>Gratuity</span><span>${split.gratuity_share.toFixed(2)}</span></div>
      {split.tip_share > 0 && (
        <div style={styles.totalRow}><span>Tip</span><span>${split.tip_share.toFixed(2)}</span></div>
      )}
      <div style={styles.grandTotalRow}><span>Total</span><span>${split.total_share.toFixed(2)}</span></div>
      <button className="no-print" style={{ ...buttonSecondary, width: "100%", marginTop: space[2] }} onClick={() => printElement(cardRef.current, "receipt")}>
        Print This Receipt
      </button>
    </div>
  );
}

const styles = {
  card: { width: 340, maxWidth: "calc(100vw - 24px)", boxSizing: "border-box", background: colors.surface, borderRadius: radius.lg, padding: space[5], boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  restaurantBlock: { textAlign: "center", marginBottom: space[3] },
  restaurantBlockCompact: { textAlign: "center", marginBottom: space[2], paddingBottom: space[2], borderBottom: `1px dashed ${colors.border}` },
  restaurantName: { fontFamily: fonts.display, fontSize: 17, fontWeight: 700, color: colors.textPrimary, wordBreak: "break-word" },
  restaurantLine: { fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  header: { textAlign: "center", marginBottom: space[3], borderBottom: `1px dashed ${colors.border}`, paddingBottom: space[3] },
  title: { fontFamily: fonts.display, margin: 0, fontSize: 18 },
  subtitle: { fontFamily: fonts.body, color: colors.textSecondary, fontSize: 13, marginTop: 4 },
  itemList: { display: "flex", flexDirection: "column", gap: 6, marginBottom: space[3] },
  itemRow: { display: "flex", justifyContent: "space-between", fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary },
  totalsBlock: { borderTop: `1px dashed ${colors.border}`, paddingTop: space[2] },
  totalRow: { display: "flex", justifyContent: "space-between", fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, padding: "2px 0" },
  grandTotalRow: { display: "flex", justifyContent: "space-between", fontFamily: fonts.display, fontSize: 16, fontWeight: 700, color: colors.textPrimary, marginTop: 6 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 },
  modal: { background: colors.surface, borderRadius: radius.lg, padding: space[5], display: "flex", flexDirection: "column", gap: space[3], maxHeight: "85vh", overflowY: "auto" },
  modeRow: { display: "flex", gap: space[2] },
  modeBtn: { flex: 1, padding: "8px 0", borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer", fontSize: 13 },
  modeBtnActive: { background: colors.brandGradient, color: "#fff", border: "none" },
  qtyRow: { display: "flex", alignItems: "center", gap: space[2] },
  splitNameRow: { display: "flex", gap: space[2], flexWrap: "wrap", marginBottom: space[2] },
  addSplitBtn: { padding: "6px 10px", fontSize: 12, borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer" },
  byItemHint: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, margin: "4px 0" },
  itemAssignList: { display: "flex", flexDirection: "column", gap: space[2] },
  itemAssignRow: { display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: space[2], borderBottom: `1px solid ${colors.border}`, gap: space[2] },
  assignBtn: { padding: "4px 8px", fontSize: 11, borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer" },
  assignBtnActive: { background: colors.brandGradient, color: "#fff", border: "none" },
  splitResultRow: { borderBottom: `1px solid ${colors.border}`, paddingBottom: space[2], marginBottom: space[2], fontFamily: fonts.body, fontSize: 13 },
  splitReceiptCard: { border: `1px dashed ${colors.border}`, borderRadius: radius.sm, padding: space[3], marginBottom: space[3] },
  splitReceiptHeader: { fontFamily: fonts.display, fontWeight: 600, marginBottom: space[2] },
  error: { color: "#b91c1c", fontSize: 13, fontFamily: fonts.body },
};
